import { ListItem, TextField } from "@mui/material";
import type { RenderFilterProps } from "@shared/frontend/hyper-table/filter-types";
import { useHyperTable } from "@shared/frontend/hyper-table/HyperTable";
import { generateHyperFilterFromPath } from "@shared/helpers/sql-relations";
import React, { useCallback } from "react";
import CustomFilter from "./CustomFilter";
import type { EntityFilterProps } from "./helpers";
import { getProcess, SELECT_OPERATORS } from "./helpers";

type StringFilterProps<T> = EntityFilterProps<T>

const StringFilter = <T extends any>(props: StringFilterProps<T>) => {
  const { filter, closeMenu } = props;
  const { getEntity } = useHyperTable<T>();

  const handleSave = useCallback(
    (
      onSave: RenderFilterProps<T>["onSave"],
      value: string,
      selectValue: string | undefined,
    ) => {
      const entity = getEntity();
      const isRelation = filter.fieldName.includes(".");

      if (isRelation && !("hyperFilter" in entity)) {
        throw new Error("No HyperFilter in entity's filters");
      }

      const process = getProcess("string", selectValue);

      onSave(
        (prev) => {
          if (value)
            return {
              meta: { ...prev.meta, text: value },
              value: isRelation
                ? (entity as any).hyperFilter(
                    generateHyperFilterFromPath(
                      entity,
                      filter.fieldName,
                      process(value),
                    ),
                  )
                : {
                    ...prev.value,
                    [filter.fieldName]: process(value),
                  },
            };
          else
            return {
              meta: { ...prev.meta, text: "" },
              value: {},
            };
        },
        { closeOnSave: false },
      );
    },
    [filter.fieldName, getEntity],
  );

  return (
    <CustomFilter<T>
      closeMenu={closeMenu}
      filter={{
        icon: "string",
        ...filter,
        select: {
          initialValue: SELECT_OPERATORS.string[0].value,
          options: SELECT_OPERATORS.string.map((v) => ({
            value: v.value,
            caption: v.caption,
          })),
          onChange: ({ meta, onSave, selectValue }) => {
            handleSave(onSave, meta.text as string, selectValue);
          },
        },
        renderFilter: useCallback(
          ({ meta, onSave, selectValue }) => (
            <ListItem
              sx={{
                ["&.Mui-focusVisible"]: { backgroundColor: "transparent" },
              }}
            >
              <TextField
                name={filter.fieldName as string}
                color="secondary"
                placeholder="חיפוש..."
                value={meta.text ?? ""}
                onChange={(e) =>
                  handleSave(onSave, e.target.value, selectValue)
                }
                autoFocus
              />
            </ListItem>
          ),
          [filter.fieldName, handleSave],
        ),
      }}
    />
  );
};

export default StringFilter;
